/* eslint-disable arrow-body-style */
import { Fragment } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import {
  Card,
  CardContent,
  ChevronDown,
  Dropdown,
  DropdownContent,
  DropdownRadioGroup,
  DropdownRadioItem,
  DropdownTrigger,
  Icon,
} from '@mesh.ai/rabbit';
import { L1_L2_NAV_WIDTH } from 'Deer/App';
import { HeaderMarginContainer } from 'Deer/components/HeaderComponents/HeaderMarginContainer';
import { toTitleCase } from 'Deer/helpers/utils';
import { IntentBreadcrumbType } from 'Deer/intent/types/IntentCommonTypes';
import { useSelectIsL2Visible } from 'Deer/slice/DeerNav.slice';
import { RenderPropSticky } from 'react-sticky-el';
import { themeColors } from 'themes/clients/baseTheme';

import { isNetchexLogin } from 'constants/netChexConstants';

import { TextHeader2, TextLabel } from 'uiToolkitV1/Typography';

import { EllipsisMenuActions } from './EllipsisMenuActions';
import { DeerBreadcrumbs } from './Layout';

type IntentFilterProps = {
  intentDropdownOptions: Array<{
    label: string;
    onClick: () => void;
    category: string;
    isDefault?: boolean;
    isBeta?: boolean;
    id?: string;
    hasMetaFilters?: boolean;
  }>;
  selectedIntent: string;
  selectedIntentId: string;
  KebabMenu?: React.ReactNode;
  MetaFilters?: React.ReactNode;
  showKebabMenu?: boolean;
  moduleName: string;
  isCategorized?: boolean;
  breadcrumbs: IntentBreadcrumbType;
  testIds?: {
    dropdownTrigger?: string;
    dropdownContent?: string;
  };
  intentTitle?: string;
  selectedIntentCategory?: string;
  showCategory?: boolean;
  ellipsisMenuOptions?: any;
};

const CategorizedDropdownUI = ({
  intentDropdownOptions,
}: {
  intentDropdownOptions: Array<{
    label: string;
    onClick: () => void;
    category: string;
    isDefault?: boolean;
    isBeta?: boolean;
    id?: string;
    hasMetaFilters?: boolean;
  }>;
}) => {
  const categoryOptionMapping = {};
  intentDropdownOptions.forEach((option) => {
    if (categoryOptionMapping[option.category]) {
      categoryOptionMapping[option.category].push(option);
    } else {
      categoryOptionMapping[option.category] = [option];
    }
  });
  const categories = Object.keys(categoryOptionMapping);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {categories?.map((category) => (
        <>
          <Box padding="8px">
            <TextLabel bold color={themeColors.text.g2}>
              {category}
            </TextLabel>
          </Box>
          {categoryOptionMapping[category]?.map(({ label, onClick, isBeta }) => (
            <DropdownRadioItem
              style={{
                marginBottom: '4px',
              }}
              key={label}
              value={label}
              onClick={onClick}
            >
              <Box py="2px" paddingLeft="8px" display="flex" justifyContent="center" alignItems="center">
                {label}
                {isBeta && (
                  <Box marginLeft="8px">
                    <BetaTag />
                  </Box>
                )}
              </Box>
            </DropdownRadioItem>
          ))}
        </>
      ))}
    </>
  );
};

const IntentFilters = ({
  KebabMenu,
  intentDropdownOptions,
  selectedIntent,
  selectedIntentId,
  MetaFilters,
  showKebabMenu = true,
  moduleName,
  isCategorized = false,
  breadcrumbs,
  testIds,
  intentTitle,
  selectedIntentCategory,
  showCategory = false,
  ellipsisMenuOptions,
}: IntentFilterProps) => {
  // this is different from hasMetaFilters because it is possible that meta filter is not there for some intents
  // As MetaFilters is common for all intents of a module, this is going to be there when even any one intent has meta filter
  const MetaFiltersExist = !!MetaFilters;
  const isL2Visible = useSelectIsL2Visible();
  const selectedIntentOption = intentDropdownOptions.find((option) => option?.id === selectedIntentId);

  let hideIntentCard = false;
  if (isNetchexLogin) {
    hideIntentCard = !selectedIntentOption?.hasMetaFilters;
  }

  return (
    <RenderPropSticky mode="top" disabled={isNetchexLogin}>
      {({ isFixed, wrapperRef, holderStyles, holderRef }) => {
        const Wrapper = isFixed ? HeaderMarginContainer : Fragment;
        return (
          <>
            <HeaderMarginContainer>
              {!isNetchexLogin && !isFixed && <DeerBreadcrumbs breadcrumbs={breadcrumbs} />}
            </HeaderMarginContainer>
            {!hideIntentCard && (
              <div ref={holderRef} style={holderStyles}>
                <Card
                  style={{
                    marginBottom: '12px',
                    padding: '12px',
                    position: isFixed ? 'fixed' : 'static',
                    top: '0',
                    left: isL2Visible ? L1_L2_NAV_WIDTH : '80px',
                    width: isFixed ? `calc(100vw - ${isL2Visible ? 326 : 80}px)` : '100%',
                    zIndex: 100,
                    borderRadius: isFixed ? '0px' : '8px',
                    transition: 'border-radius 0.2s',
                  }}
                  ref={wrapperRef}
                >
                  <Box
                    sx={{
                      '&>div': {
                        width: '100%',
                      },
                    }}
                    w="100%"
                  >
                    <Wrapper>
                      <CardContent style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {!isNetchexLogin && isFixed && <DeerBreadcrumbs breadcrumbs={breadcrumbs} />}
                        {!isNetchexLogin && (
                          <Flex minH="40px" alignItems="center">
                            {(intentTitle.length > 0 || moduleName.length > 0) && (
                              <TextHeader2 bold mr="12px">
                                {intentTitle || toTitleCase(moduleName)}:
                              </TextHeader2>
                            )}
                            {intentDropdownOptions.length === 1 ? (
                              <Flex alignItems="center">
                                <TextHeader2 bold>{selectedIntent}</TextHeader2>
                              </Flex>
                            ) : (
                              <Dropdown>
                                <Box
                                  sx={{
                                    'button[data-state="open"]': {
                                      borderBottom: '1px solid #0D819F',
                                    },
                                    'button[data-state="closed"]': {
                                      borderBottom: '1px dashed #999999',
                                    },
                                    'button[data-state="open"] .icon': {
                                      color: '#0D819F',
                                      transform: 'rotate(180deg)',
                                      transition: 'transform 0.2s',
                                    },
                                    "button[data-state='closed'] .icon": {
                                      color: '#333333',
                                    },
                                  }}
                                >
                                  <DropdownTrigger
                                    style={{
                                      minWidth: '450px',
                                    }}
                                    data-testid={testIds?.dropdownTrigger}
                                  >
                                    <Flex alignItems="center">
                                      {showCategory && selectedIntentCategory ? (
                                        <TextHeader2 bold>
                                          [{selectedIntentCategory}] {selectedIntent}
                                        </TextHeader2>
                                      ) : (
                                        <TextHeader2 bold>{selectedIntent}</TextHeader2>
                                      )}
                                      <Box ml="auto">
                                        <Box>
                                          <Icon className="icon" IconSVG={ChevronDown} />
                                        </Box>
                                      </Box>
                                    </Flex>
                                  </DropdownTrigger>
                                </Box>
                                <DropdownContent
                                  data-testid={testIds?.dropdownContent}
                                  align="start"
                                  style={{ minWidth: '450px', padding: '4px' }}
                                >
                                  <DropdownRadioGroup value={selectedIntent}>
                                    {isCategorized ? (
                                      <CategorizedDropdownUI intentDropdownOptions={intentDropdownOptions} />
                                    ) : (
                                      intentDropdownOptions?.map(({ label, onClick, isBeta }) => (
                                        <DropdownRadioItem
                                          style={{
                                            marginBottom: '4px',
                                          }}
                                          key={label}
                                          value={label}
                                          onClick={onClick}
                                        >
                                          {label}
                                          {isBeta && (
                                            <Box marginLeft="8px">
                                              <BetaTag />
                                            </Box>
                                          )}
                                        </DropdownRadioItem>
                                      ))
                                    )}
                                  </DropdownRadioGroup>
                                </DropdownContent>
                              </Dropdown>
                            )}
                          </Flex>
                        )}
                        <Box
                          display="flex"
                          flexDirection="row"
                          gap="8px"
                          alignItems="flex-start"
                          justifyContent="space-between"
                        >
                          <Box>{MetaFiltersExist && MetaFilters}</Box>
                          {showKebabMenu && (
                            <Box display="flex" gap="8px" justifyContent="flex-end">
                              <EllipsisMenuActions options={ellipsisMenuOptions} />
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Wrapper>
                  </Box>
                </Card>
              </div>
            )}
          </>
        );
      }}
    </RenderPropSticky>
  );
};

export { IntentFilters };
